import type {
  CategorizationItem,
  ElectronicSignatureBlock,
  LinkBlock,
  PolicyAcknowledgementBlock,
} from '@course-authoring/shared-types';

/**
 * NA-319: small, non-cryptographic content fingerprint (32-bit FNV-1a,
 * base36). Used only to detect whether the content a restored answer
 * depends on (a Categorization block's correct-category data, or a Policy/
 * Electronic Signature block's document/attestation text) still matches
 * what's currently authored — not a security boundary, just change
 * detection, so collision resistance beyond "good enough to not false-match
 * on a real content edit" isn't a design goal.
 */
function fingerprint(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Fingerprints a Categorization answer (item id -> chosen category id),
 * order-independent so it matches regardless of whether it's computed from
 * a live submission (drag order) or reconstructed from `block.items` (author
 * order). Called with the SAME map shape on both ends: at submit time with
 * the learner's just-submitted (guaranteed fully correct) assignments, and
 * at restore time with `item.id -> item.correctCategoryId` reconstructed
 * from the block's CURRENT content — a match proves the learner's real
 * correct answer still matches what's currently authored.
 */
export function fingerprintCategorizationAnswer(assignments: Record<string, string>): string {
  const sorted = Object.entries(assignments).sort(([a], [b]) => a.localeCompare(b));
  return fingerprint(JSON.stringify(sorted));
}

/** Reconstructs the current fully-correct assignment map from a Categorization block's own content. */
export function correctCategorizationAssignments(
  items: CategorizationItem[]
): Record<string, string> {
  return Object.fromEntries(items.map((item) => [item.id, item.correctCategoryId]));
}

/**
 * Fingerprints "what the learner acknowledged" for an Electronic Signature /
 * Policy Acknowledgement / Link (NA-409) block, so a republish that changes
 * the document, attestation text, or link target invalidates a prior
 * acknowledgement instead of it silently carrying over forever.
 */
export function fingerprintAcknowledgementContent(
  block: ElectronicSignatureBlock | PolicyAcknowledgementBlock | LinkBlock
): string {
  const content = block.type === 'policy-acknowledgement'
    ? `${block.title}|${block.policyType}|${block.policyUrl}|${JSON.stringify(block.description)}`
    : block.type === 'link'
    ? `${block.title ?? ''}|${block.linkText ?? ''}|${block.linkUrl ?? ''}|${JSON.stringify(block.description ?? '')}`
    : `${block.title}|${JSON.stringify(block.description)}|${block.checkboxLabel}`;
  return fingerprint(content);
}
